import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useBrand } from '@/hooks/useBrand';
import {
  fetchCodesByOwner,
  listCommissionsForParrain,
  type CodeParrainage,
  type CommissionParrainage,
  typePartenaireLabel,
  typePartenaireIcon,
} from '@/lib/parrainage';

// ─────────────────────────────────────────────────────────────
// Espace « Revendeur » — depuis le tableau de bord.
// Le revendeur retrouve :
//   • son (ses) code(s) de parrainage (à partager) ;
//   • ses filleuls (clients qu'il a ramenés) ;
//   • ses commissions (gains crédités dans son portefeuille).
// ─────────────────────────────────────────────────────────────

interface Filleul {
  id: number;
  name: string | null;
  nomcommerce: string | null;
  email: string | null;
  code_parrainage: string | null;
  datecreation: string | null;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

function StatCard({ icon, label, value, tone }: { icon: string; label: string; value: string; tone: string }) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
      <div className={`w-10 h-10 rounded-lg ${tone} flex items-center justify-center mb-3`}>
        <i className={`${icon} text-lg`}></i>
      </div>
      <div className="text-2xl font-bold font-heading text-foreground-950 tabular-nums">{value}</div>
      <div className="text-xs text-foreground-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function RevendeurPage() {
  const { user } = useAuth();
  const { brand } = useBrand();
  const [codes, setCodes] = useState<CodeParrainage[]>([]);
  const [commissions, setCommissions] = useState<CommissionParrainage[]>([]);
  const [filleuls, setFilleuls] = useState<Filleul[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const monaie = user?.monaie || 'MAD';

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const [c, com, f] = await Promise.all([
        fetchCodesByOwner(user.id),
        listCommissionsForParrain(user.id),
        supabase
          .from('users')
          .select('id, name, nomcommerce, email, code_parrainage, datecreation')
          .eq('parrain_id', user.id)
          .order('datecreation', { ascending: false })
          .then(({ data }) => (data || []) as Filleul[]),
      ]);
      setCodes(c);
      setCommissions(com);
      setFilleuls(f);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Espace revendeur'; }, []);

  const totalCommissions = useMemo(
    () => commissions.reduce((s, c) => s + (Number(c.commission) || 0), 0),
    [commissions],
  );

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      window.setTimeout(() => setCopiedCode(null), 2000);
    } catch { /* presse-papiers indisponible */ }
  };

  const shareText = (code: string) =>
    `Rejoins ${brand.name} avec mon code de parrainage ${code} et profite d'avantages sur ton abonnement !`;

  if (!user) return null;

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-accent-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-share-circle-line text-xl text-background-50"></i>
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Espace revendeur</h1>
            <p className="text-sm text-foreground-500">Votre code, vos filleuls et vos commissions.</p>
          </div>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap w-fit"
        >
          <i className="ri-refresh-line"></i>
          Actualiser
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : (
        <>
          {/* Statistiques */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
            <StatCard icon="ri-coupon-3-line" label="Codes actifs" value={String(codes.filter((c) => c.actif).length)} tone="bg-primary-100 text-primary-700" />
            <StatCard icon="ri-user-add-line" label="Filleuls" value={String(filleuls.length)} tone="bg-secondary-100 text-secondary-900" />
            <StatCard icon="ri-share-forward-line" label="Commissions" value={String(commissions.length)} tone="bg-accent-100 text-accent-700" />
            <StatCard
              icon="ri-money-dollar-circle-line"
              label="Total gagné"
              value={`${Math.round(totalCommissions).toLocaleString()} ${monaie}`}
              tone="bg-primary-100 text-primary-700"
            />
          </div>

          {/* Mes codes */}
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
              <i className="ri-coupon-3-line text-primary-500"></i>
              Mon code de parrainage
            </h2>
            {codes.length === 0 ? (
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-coupon-3-line text-xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-600">Aucun code de parrainage ne vous est encore attribué.</p>
                <p className="text-xs text-foreground-400 mt-1">Contactez l&apos;administrateur {brand.name} pour obtenir votre code revendeur.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {codes.map((c) => (
                  <div key={c.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-accent-100 text-accent-700">
                        <i className={typePartenaireIcon(c.typePartenaire)}></i>
                        {typePartenaireLabel(c.typePartenaire)}
                      </span>
                      <span className={`text-[11px] font-semibold ${c.actif ? 'text-accent-700' : 'text-foreground-400'}`}>
                        {c.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <code className="text-lg font-bold tracking-wider text-foreground-950 bg-background-100 px-3 py-1.5 rounded-lg flex-1 text-center">
                        {c.code}
                      </code>
                      <button
                        onClick={() => copyCode(c.code)}
                        title="Copier le code"
                        className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer flex-shrink-0"
                      >
                        <i className={copiedCode === c.code ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                      </button>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-foreground-500 mb-3">
                      <span className="flex items-center gap-1"><i className="ri-percent-line"></i>{c.pourcentage}% de commission</span>
                      <span className="flex items-center gap-1"><i className="ri-gift-line"></i>{c.moisGratuits} mois offert{c.moisGratuits > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(shareText(c.code))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-green-500 text-white text-xs font-semibold hover:bg-green-600 transition-colors cursor-pointer no-underline whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-line"></i>Partager
                      </a>
                      <a
                        href={`mailto:?subject=${encodeURIComponent(`Code parrainage ${brand.name}`)}&body=${encodeURIComponent(shareText(c.code))}`}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold hover:bg-background-200/70 transition-colors cursor-pointer no-underline whitespace-nowrap"
                      >
                        <i className="ri-mail-line"></i>Email
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Filleuls */}
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
              <i className="ri-user-add-line text-primary-500"></i>
              Mes filleuls ({filleuls.length})
            </h2>
            {filleuls.length === 0 ? (
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <p className="text-sm text-foreground-500">Aucun filleul pour le moment. Partagez votre code pour commencer à gagner des commissions.</p>
              </div>
            ) : (
              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="bg-background-100/60 text-left">
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Filleul</th>
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Email</th>
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Code utilisé</th>
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Inscrit le</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filleuls.map((f) => (
                        <tr key={f.id} className="border-t border-background-200/70">
                          <td className="px-4 py-3 text-foreground-900">{f.nomcommerce || f.name || `#${f.id}`}</td>
                          <td className="px-4 py-3 text-foreground-600">{f.email || '—'}</td>
                          <td className="px-4 py-3 text-foreground-600"><code className="text-xs">{f.code_parrainage || '—'}</code></td>
                          <td className="px-4 py-3 text-foreground-500 text-xs">{formatDate(f.datecreation)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Commissions */}
          <div>
            <h2 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
              <i className="ri-share-forward-line text-primary-500"></i>
              Historique des commissions
            </h2>
            {commissions.length === 0 ? (
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <p className="text-sm text-foreground-500">Aucune commission pour le moment.</p>
              </div>
            ) : (
              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="bg-background-100/60 text-left">
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Date</th>
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Filleul</th>
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Abonnement</th>
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Montant payé</th>
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Taux</th>
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Ma commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((c) => (
                        <tr key={c.id} className="border-t border-background-200/70">
                          <td className="px-4 py-3 text-foreground-500 text-xs">{formatDate(c.createdAt)}</td>
                          <td className="px-4 py-3 text-foreground-900">{c.filleulName || (c.filleulId ? `#${c.filleulId}` : '—')}</td>
                          <td className="px-4 py-3 text-foreground-600">{c.plan || '—'}</td>
                          <td className="px-4 py-3 text-foreground-600">{Number(c.montant).toLocaleString()} {monaie}</td>
                          <td className="px-4 py-3 text-foreground-600">{c.pourcentage}%</td>
                          <td className="px-4 py-3 text-sm font-bold text-accent-700">+{Number(c.commission).toLocaleString()} {monaie}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}